import React from 'react';
import { Box, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { styled } from '@mui/material/styles';
import { PropertyField } from '../../../system/field';
import { generateDataTestId } from '../../../utils';
import { ControlField } from '../../../contexts/MetadataContext';
import { useFieldUpdater } from '@brightmls/nextgen.lae.flc';

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  margin: 0,
  '& .MuiTypography-root': {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.body1.fontWeight,
    color: theme.palette.text.primary,
  },
  '& .MuiRadio-root': {
    transform: 'scale(1.2)',
    transformOrigin: 'center',
    '&.Mui-checked': {
      color: theme.palette.secondary.main,
    },
    '&.Mui-disabled': {
      color: theme.palette.grey[300],
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  '&.Mui-disabled': {
    '& .MuiTypography-root': {
      color: theme.palette.text.disabled,
    },
  },
}));

interface BooleanRadioFieldProps {
  value: boolean | null;
  onChange: (value: boolean | null) => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  field: ControlField;
  fieldLabel: string;
}

const BooleanRadioField: React.FC<BooleanRadioFieldProps> = ({
  value,
  onChange,
  disabled = false,
  readOnly = false,
  field,
  fieldLabel,
  error = false,
}) => {
  const update = useFieldUpdater();

  const radioValue = value === null ? '' : value.toString();

  const handleClick = (clickedValue: string) => {
    if (readOnly) return;

    // Capture if the current value is enabled
    const wasEnabled = Boolean(value);

    // Convert the clicked value to boolean for comparison
    const boolValue = clickedValue === 'true';
    const normalizedValue =
      typeof value === 'number' && value === 1
        ? true
        : typeof value === 'number' && value === 0
          ? false
          : value;

    // If clicking the already selected value, set to null
    if (normalizedValue === boolValue) {
      if (field.Key === PropertyField.BASEMENT_YN) {
        update({
          [PropertyField.BASEMENT]: [],
        });
      }

      if (field.Key === PropertyField.REAL_ESTATE_OWNED_YN) {
        // Unsetting the REO field
        // This should have been done by the rule engine
        // Will be fixed in MARK-1385
        if (wasEnabled) {
          update({
            [PropertyField.REAL_ESTATE_OWNED_YN]: null,
            [PropertyField.SALE_TYPE]: [],
          });
        } else {
          update({
            [PropertyField.REAL_ESTATE_OWNED_YN]: null,
          });
        }
      } else {
        onChange(null);
      }
    } else {
      if (field.Key === PropertyField.REAL_ESTATE_OWNED_YN) {
        // Simulate RULE ENGINE behaviour for REO
        // This should have been done by the rule engine
        // Will be fixed in MARK-1385
        if (boolValue) {
          update({
            [PropertyField.REAL_ESTATE_OWNED_YN]: true,
            [PropertyField.SHORT_SALE]: 'No',
            [PropertyField.SALE_TYPE]: ['Bank Owned/REO'],
          });
        } else {
          if (wasEnabled) {
            update({
              [PropertyField.SALE_TYPE]: [],
              [PropertyField.REAL_ESTATE_OWNED_YN]: false,
            });
          } else {
            update({
              [PropertyField.REAL_ESTATE_OWNED_YN]: false,
            });
          }
        }
      } else {
        onChange(boolValue);
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '40px',
      }}
    >
      <RadioGroup
        data-testid={generateDataTestId(fieldLabel, undefined, 'radioField')}
        row
        value={radioValue}
      >
        <StyledFormControlLabel
          value="true"
          control={
            <Radio
              size="small"
              checked={
                value === true || (typeof value === 'number' && value === 1)
              }
              onClick={() => handleClick('true')}
            />
          }
          label="Yes"
          disabled={disabled || readOnly}
        />
        <StyledFormControlLabel
          value="false"
          control={
            <Radio
              size="small"
              checked={
                value === false || (typeof value === 'number' && value === 0)
              }
              onClick={() => handleClick('false')}
            />
          }
          sx={{ marginLeft: '30px' }}
          label="No"
          disabled={disabled || readOnly}
        />
      </RadioGroup>
    </Box>
  );
};

export default BooleanRadioField;
